import {useMemo} from "react";

import {useApp2Auth} from "@/app2/auth";

import {jwtDecode} from "jwt-decode";

import {Role} from "@buildwitt/identity-client";

/** Org shape expected by callers using {@link authInfo.orgHelper.getOrgs}. */
export type SyntheticOrgMemberInfo = {
	orgId: string;
	orgName: string;
	userInheritedRolesPlusCurrentRole: string[];
	userAssignedAdditionalRoles: string[];
};

const createOrgHelperFromJwt = (jwt?: string) => {
	const getOrgs = (): SyntheticOrgMemberInfo[] => {
		if (!jwt) return [];
		try {
			const decoded: {
				org_id_to_org_member_info?: Record<
					string,
					{
						org_name?: string;
						inherited_user_roles_plus_current_role?: string[];
						additional_roles?: string[];
					}
				>;
			} = jwtDecode(jwt);
			const raw = decoded?.org_id_to_org_member_info ?? {};
			return Object.entries(raw).map(([orgId, info]) => ({
				orgId,
				orgName: info?.org_name ?? "",
				userInheritedRolesPlusCurrentRole: info?.inherited_user_roles_plus_current_role ?? [],
				userAssignedAdditionalRoles: info?.additional_roles ?? []
			}));
		} catch {
			return [];
		}
	};
	return {
		getOrgs,
		getOrgIds: (): string[] => getOrgs().map((o) => o.orgId)
	};
};

/**
 * Session-derived auth helpers for App2 (manual PropelAuth OAuth — JWT from {@link useApp2Auth}).
 */
const usePropelAuth = () => {
	const {jwt, user, authenticated} = useApp2Auth();

	const authUser =
		authenticated && user && jwt
			? {
					userId: (user as {externalId?: string}).externalId ?? user.username ?? "",
					email: user.email ?? "",
					firstName: user.firstname ?? "",
					lastName: user.lastname ?? ""
				}
			: undefined;

	const orgHelper = useMemo(() => createOrgHelperFromJwt(jwt), [jwt]);

	let roles: string[] = [];
	const orgs = orgHelper.getOrgs();
	if (orgs?.length) {
		roles = roles.concat(orgs[0].userInheritedRolesPlusCurrentRole);
		roles = roles.concat(orgs[0].userAssignedAdditionalRoles);
	}

	const authInfo = useMemo(
		() => ({
			loading: false,
			isLoggedIn: !!(authenticated && jwt),
			user: authUser,
			orgHelper
		}),
		[authenticated, jwt, authUser, orgHelper]
	);

	return {
		loading: false,
		authUser,
		isAdmin: !!(
			roles.length &&
			(roles.includes(Role["Training::Admin"]) || roles.includes(Role["BuildWitt:Admin"]))
		),
		isBuildWittAdmin: !!(roles.length && roles.includes(Role["BuildWitt:Admin"])),
		authInfo,
		isTrainingMember: !!(
			roles.length &&
			(roles.includes(Role["Training::Admin"]) ||
				roles.includes(Role["BuildWitt:Admin"]) ||
				roles.includes(Role["Training::Student"]))
		),
		isIdentityMember: !!(
			roles.length &&
			(roles.includes(Role["Identity::Admin"]) ||
				roles.includes(Role["BuildWitt:Admin"]) ||
				roles.includes(Role["Identity::Member"]))
		)
	};
};

export default usePropelAuth;
